import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Skeleton
} from '@mui/material';
import {
  Medication as MedicationIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  increment,
  writeBatch,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';

import { COLORS } from '../../theme/constants';

const { primary: PRIMARY, teal: TEAL, warning: AMBER, danger: DANGER, success: SUCCESS, info: INFO } = COLORS;

/**
 * Hook personalizado para estadísticas de farmacia en tiempo real
 */
const usePharmaStats = () => {
  const [stats, setStats] = useState({
    totalProducts: 0, lowStockItems: 0, expiringSoon: 0,
    controlledSubstances: 0, todayDispensations: 0, pendingOrders: 0,
    cogsMonth: 0, totalInventoryValue: 0,
  });
  const [alerts, setAlerts] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubs = [];

    // Real-time: alertas no resueltas
    unsubs.push(onSnapshot(
      query(collection(db, 'stock_alerts'), where('isResolved', '==', false),
        orderBy('createdAt', 'desc'), limit(10)),
      (snap) => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAlerts(data);
        setStats(p => ({
          ...p,
          lowStockItems: data.filter(a => a.alertType === 'low_stock').length,
          expiringSoon: data.filter(a => a.alertType === 'expiring').length,
        }));
      }
    ));

    // Real-time: transacciones de hoy
    const todayStart = new Date(); todayStart.setHours(0,0,0,0);
    unsubs.push(onSnapshot(
      query(collection(db, 'inventory_transactions'),
        where('timestamp', '>=', todayStart),
        where('type', '==', 'dispensation'),
        orderBy('timestamp', 'desc'), limit(15)),
      (snap) => {
        setRecentTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setStats(p => ({ ...p, todayDispensations: snap.size }));
        setLoading(false);
      }
    ));

    // One-time: productos activos
    getDocs(query(collection(db, 'products'), where('isActive', '==', true)))
      .then(s => setStats(p => ({ ...p, totalProducts: s.size,
        controlledSubstances: s.docs.filter(d => d.data().isControlled).length })));

    // One-time: COGS del mes actual
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
    getDocs(query(collection(db, 'inventory_transactions'),
      where('type', '==', 'dispensation'), where('timestamp', '>=', monthStart)))
      .then(s => {
        const cogs = s.docs.reduce((sum, d) => sum + (d.data().totalCost || 0), 0);
        setStats(p => ({ ...p, cogsMonth: cogs }));
      });

    return () => unsubs.forEach(u => u());
  }, []);

  return { stats, alerts, recentTransactions, loading };
};

/**
 * Dashboard de Farmacia - Primera pestaña
 * Vista general y acciones rápidas
 */
const PharmacyDashboard = () => {
  const navigate = useNavigate();
  const { stats, alerts, recentTransactions, loading } = usePharmaStats();

  const quickActions = [
    {
      title: 'Dispensar Insumo',
      description: 'Registro rápido de uso de medicamentos e insumos',
      icon: <MedicationIcon />,
      color: 'primary',
      action: () => navigate('/pharmacy/dispense')
    },
    {
      title: 'Gestionar Inventario',
      description: 'Control de stock, lotes y ubicaciones',
      icon: <InventoryIcon />,
      color: 'secondary',
      action: () => console.log('Ir a inventario')
    },
    {
      title: 'Transferir Stock',
      description: 'Mover insumos entre ubicaciones',
      icon: <LocalShippingIcon />,
      color: 'info',
      action: () => console.log('Transferir stock')
    },
    {
      title: 'Ver Reportes',
      description: 'Análisis de consumo y tendencias',
      icon: <TrendingUpIcon />,
      color: 'success',
      action: () => console.log('Ver reportes')
    }
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1,2,3,4,5,6].map(i => (
            <Grid
              key={i}
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}>
              <Skeleton variant="rectangular" height={100} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alerta de urgencia si hay alertas críticas */}
      {alerts.length > 0 && alerts.some(a => a.alertType === 'low_stock') && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {alerts.filter(a => a.alertType === 'low_stock').length} producto(s) en stock crítico requieren atención inmediata
        </Alert>
      )}
      {/* Estadísticas principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalProducts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Productos
                  </Typography>
                </Box>
                <MedicationIcon color="primary" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {stats.lowStockItems}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stock Crítico
                  </Typography>
                </Box>
                <WarningIcon color="error" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="warning">
                    {stats.expiringSoon}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por Vencer
                  </Typography>
                </Box>
                <WarningIcon color="warning" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="secondary">
                    {stats.controlledSubstances}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Controlados
                  </Typography>
                </Box>
                <WarningIcon color="secondary" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success">
                    {stats.todayDispensations}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hoy
                  </Typography>
                </Box>
                <MedicationIcon color="success" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="info">
                    ${stats.cogsMonth.toLocaleString('es-CL')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    COGS Mes
                  </Typography>
                </Box>
                <TrendingUpIcon color="info" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Grid principal */}
      <Grid container spacing={3}>
        {/* Acciones Rápidas */}
        <Grid
          size={{
            xs: 12,
            md: 8
          }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Acciones Rápidas
          </Typography>
          
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid
                key={index}
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={action.action}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 1, 
                        bgcolor: `${action.color}.main`,
                        color: 'white',
                        mr: 2
                      }}>
                        {action.icon}
                      </Box>
                      <Typography variant="h6" fontWeight="bold">
                        {action.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Alertas */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Alertas Activas
            </Typography>
            <Chip label={alerts.length} size="small" color={alerts.length > 0 ? 'error' : 'default'} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {alerts.length === 0 ? (
              <Alert severity="success">Sin alertas activas</Alert>
            ) : (
              alerts.map((alert) => (
                <Alert
                  key={alert.id}
                  severity={alert.alertType === 'low_stock' ? 'error' : 'warning'}
                  sx={{ mb: 1 }}
                  action={
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => {
                        updateDoc(doc(db, 'stock_alerts', alert.id), {
                          isResolved: true,
                          resolvedAt: serverTimestamp(),
                          resolvedBy: 'current-user-id'
                        });
                      }}
                    >
                      Resolver
                    </Button>
                  }
                >
                  {alert.message}
                </Alert>
              ))
            )}
          </Box>
        </Grid>

        {/* Transacciones Recientes */}
        <Grid size={12}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Transacciones Recientes
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {recentTransactions.length === 0 ? (
              <Alert severity="info">Sin transacciones hoy</Alert>
            ) : (
              recentTransactions.map((tx) => {
                const timeAgo = tx.timestamp?.toDate
                  ? new Date(tx.timestamp.toDate()).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
                  : '-';
                return (
                  <Card key={tx.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {tx.productId || 'Producto desconocido'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cantidad: {Math.abs(tx.quantity)} {tx.unitOfMeasure || ''} | Paciente: {tx.patientId || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Dr. {tx.doctorId || '-'} | {tx.location || '-'}
                        </Typography>
                      </Box>
                      <Chip label={timeAgo} size="small" />
                    </Box>
                  </Card>
                );
              })
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PharmacyDashboard;
